<template>
  <div ref="carouselScrollTarget" class="ui-carousel-scroll">
    <component
      :is="tag"
      ref="scrollTarget"
      class="ui-carousel-flex"
      :style="{ gap: `${gap}px` }"
    >
      <slot />
    </component>
  </div>
</template>

<script lang="ts" setup>
// 子要素が入れ子になると初期化に失敗することがある

interface Props {
  nextButton?: HTMLElement
  prevButton?: HTMLElement
  tag?: string
  gap?: number
}

const props = withDefaults(defineProps<Props>(), {
  nextButton: null,
  prevButton: null,
  tag: 'div',
  gap: 16,
})

const scrollTarget = ref<HTMLElement>()
const carouselScrollTarget = ref<HTMLElement>()
let observer = null

// カルーセル内で一度に表示可能な子要素の数を返す
// 全体が表示されているものだけカウント。一部隠れているものはカウントしない。
const getDisplayCount = () => {
  if (!scrollTarget.value.children) {
    return 1
  }
  const children = scrollTarget.value.children
  const childWidth = (children[0] as HTMLElement).offsetWidth
  const targetWidth = scrollTarget.value.clientWidth
  return Math.max(
    1,
    Math.floor((targetWidth + props.gap * 2) / (childWidth + props.gap)),
  )
}

onMounted(() => {
  nextTick(() => {
    setSlideUI(
      scrollTarget.value,
      carouselScrollTarget.value,
      props.nextButton,
      props.prevButton,
    )
  })
})

onUnmounted(() => {
  observer?.disconnect()
})

const setSlideUI = (
  _scrollTarget: HTMLElement,
  _carouselScrollTarget: HTMLElement,
  nextButton: HTMLElement,
  prevButton: HTMLElement,
) => {
  const calculatePosition = (position?: number, move?: number): number => {
    try {
      if (_scrollTarget.children.length < getDisplayCount()) {
        return 0
      }
      const children = _scrollTarget.children
      const childWidth = (children[0] as HTMLElement).offsetWidth

      if (!position) {
        // positionの指定がない場合は今の位置から計算する
        const scrollLeft = _carouselScrollTarget.scrollLeft
        position = Math.floor(scrollLeft / childWidth) + 1
      }

      if (move) {
        position = position + move
      }

      const value = (position - 1) * (childWidth + props.gap)
      return Math.max(0, value)
    } catch {
      return 0
    }
  }

  const updateButtonState = () => {
    const scrollLeft = _carouselScrollTarget.scrollLeft
    const scrollWidth = _carouselScrollTarget.scrollWidth
    const clientWidth = _carouselScrollTarget.clientWidth
    if (scrollLeft == 0) {
      props.prevButton?.setAttribute('disabled', '')
    } else if (scrollWidth - scrollLeft <= clientWidth) {
      props.nextButton?.setAttribute('disabled', '')
    } else {
      props.prevButton?.removeAttribute('disabled')
      props.nextButton?.removeAttribute('disabled')
    }
  }

  const updateScroll = (position?: number, move?: number) => {
    _carouselScrollTarget.scrollLeft = calculatePosition(position, move)
    updateButtonState()
  }

  _carouselScrollTarget.addEventListener('scroll', () => {
    updateButtonState()
  })

  // 子要素の変更を監視し、positionとボタンの状態を更新する
  observer = new MutationObserver(() => {
    updateButtonState()
  })
  observer.observe(_scrollTarget, { subtree: false, childList: true })

  /**
   * ボタン用イベント
   * positionの更新、transitionの切り替え
   */
  nextButton?.addEventListener('click', e => {
    e.preventDefault()
    updateScroll(null, getDisplayCount())
  })

  prevButton?.addEventListener('click', e => {
    e.preventDefault()
    updateScroll(null, -getDisplayCount())
  })

  updateButtonState()
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.ui-carousel-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
}
.ui-carousel-flex {
    display: flex;
}
</style>